import {
    useInfiniteQuery,
    useQuery,
    useSuspenseInfiniteQuery,
    useSuspenseQuery,
} from '@tanstack/react-query'
import {Suspense, useCallback, useMemo, useState} from 'react'
import {createFileRoute, Link} from '@tanstack/react-router'
import {createTranslator, useTranslations} from 'use-intl'
import {useIntersectionObserver} from 'usehooks-ts'
import {zodValidator} from '@tanstack/zod-adapter'
import clsx from 'clsx'
import {z} from 'zod'
import {
    getProductDefaultAppearanceId,
    getProductDefaultViewId,
    getProductModelImageSrc,
    getProductModelImageSrcSet,
} from '~/utils/product'
import MobileFilterDialog from '~/components/product-list/MobileFilterDialog'
import BaseFilterBadgeList from '~/components/product-list/FilterBadgeList'
import Filter, {ProductFilters} from '~/components/product-list/Filter'
import {productFilterQueryOptions} from '~/client/fn/productFilter'
import {defaultImagesQueryOptions} from '~/client/fn/defaultImages'
import {translationsQueryOptions} from '~/client/fn/translations'
import {productsInfiniteQueryOptions} from '~/client/fn/products'
import ImageComposition from '~/components/ImageComposition'
import Breadcrumbs from '~/components/common/Breadcrumbs'
import useLocaleParams from '~/hooks/useLocaleParams'
import {Button} from '~/components/common/Button'
import useShopConfig from '~/hooks/useShopConfig'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'
import {buildBreadcrumbJsonLd, jsonLdScript} from '~/utils/jsonLd'
import seo from '~/utils/seo'

const productListSchema = z.object({
    department: z
        .string()
        .regex(/D[0-9]+/)
        .optional(),
    category: z
        .string()
        .regex(/P[0-9]+/)
        .optional(),
    appearance: z
        .string()
        .regex(/C[a-zA-Z]+/)
        .optional(),
    size: z
        .string()
        .regex(/SS[0-9]+/)
        .optional(),
    design: z.string().optional(),
    collection: z
        .string()
        .regex(/T[a-zA-Z]+/)
        .optional(),
})

export const Route = createFileRoute('/$lang/_layout/products/')({
    validateSearch: zodValidator(productListSchema),
    component: RouteComponent,
    shouldReload: false,
    loader: async ({context}) => {
        return await context.queryClient.ensureQueryData(translationsQueryOptions(context.locale))
    },
    head: ({loaderData, params: {lang}}) => {
        if (!loaderData) {
            return {}
        }

        const canonicalUrl = `https://gravity-shirts.com/${lang}/products`
        const t = createTranslator({locale: lang || 'en', messages: loaderData})
        const metaTags = seo({
            title: t('productList.title'),
            description: t('productList.description'),
            ogUrl: canonicalUrl,
            locale: lang,
        })

        const breadcrumbJsonLd = buildBreadcrumbJsonLd([
            {name: 'Home', url: `https://gravity-shirts.com/${lang}`},
            {name: t('productList.title'), url: canonicalUrl},
        ])

        return {
            meta: metaTags.length > 0 ? metaTags : undefined,
            scripts: [jsonLdScript(breadcrumbJsonLd)],
        }
    },
})

function RouteComponent() {
    const t = useTranslations('productList')
    const navigate = Route.useNavigate()
    const search = Route.useSearch()

    const [isMobileFilterVisible, setIsMobileFilterVisible] = useState<boolean>(false)

    const applyFilters = useCallback(
        (filter: ProductFilters) => {
            void navigate({
                resetScroll: true,
                search: ({design, collection}) => ({
                    design: design,
                    collection: collection,
                    department: filter.departmentId,
                    category: filter.categoryId,
                    appearance: filter.appearanceId,
                    size: filter.sizeId,
                }),
            })
        },
        [navigate]
    )

    const closeMobileFilter = useCallback(
        (filter: ProductFilters) => {
            applyFilters(filter)
            setIsMobileFilterVisible(false)
        },
        [applyFilters]
    )

    return (
        <div className="px-4 py-6 mx-auto max-w-screen-xl lg:py-12 sm:px-6">
            <Breadcrumbs
                className="mb-2 md:mb-6 md:mx-4"
                items={[
                    {name: t('breadcrumbs.home'), to: '/'},
                    {name: t('breadcrumbs.products'), to: '.'},
                ]}
            />
            <h1 className="sr-only">{t('title')}</h1>
            <div className="md:grid md:gap-6 md:grid-cols-3 lg:grid-cols-4">
                <div className="hidden md:inline-block">
                    <Suspense fallback={<DesktopFilterSkeletonLoader />}>
                        <DesktopFilter onSelect={applyFilters} />
                    </Suspense>
                </div>
                <div className="md:col-span-2 lg:col-span-3">
                    <h2 className="sr-only">{t('activeFilters')}</h2>
                    <div className="mb-6 empty:hidden">
                        <Suspense>
                            <FilterBadgeList onSelect={applyFilters} />
                        </Suspense>
                    </div>
                    <h2 className="sr-only">{t('activeProducts')}</h2>
                    <Suspense fallback={<ProductListSkeletonLoader />}>
                        <ProductList />
                    </Suspense>
                </div>
            </div>

            <div className="pb-4 pt-6 sticky bottom-0 md:hidden">
                <Button className="w-full" onClick={() => setIsMobileFilterVisible(true)}>
                    {t('filter.action')}
                </Button>
            </div>
            <MobileFilterDialog
                open={isMobileFilterVisible}
                onClose={closeMobileFilter}
                activeFilter={{
                    departmentId: search.department,
                    categoryId: search.category,
                    appearanceId: search.appearance,
                    sizeId: search.size,
                }}
            />
        </div>
    )
}

const useProductFilter = () => {
    const params = useLocaleParams()
    const search = Route.useSearch()
    const [initialSearch] = useState(search)

    const {data: initialFilter} = useSuspenseQuery(
        productFilterQueryOptions(
            initialSearch.department,
            initialSearch.category,
            initialSearch.appearance,
            initialSearch.size,
            initialSearch.design,
            initialSearch.collection,
            params.locale
        )
    )

    const {data: loadedFilter} = useQuery(
        productFilterQueryOptions(
            search.department,
            search.category,
            search.appearance,
            search.size,
            search.design,
            search.collection,
            params.locale
        )
    )

    const filter = useMemo(() => loadedFilter ?? initialFilter, [loadedFilter, initialFilter])
    return {filter, search}
}

const useProducts = () => {
    const params = useLocaleParams()
    const search = Route.useSearch()
    const [initialSearch] = useState(search)

    const {data: initialProducts} = useSuspenseInfiniteQuery(
        productsInfiniteQueryOptions(
            initialSearch.department,
            initialSearch.category,
            initialSearch.appearance,
            initialSearch.size,
            initialSearch.design,
            initialSearch.collection,
            params.locale
        )
    )

    const {
        data: loadedProducts,
        fetchNextPage,
        hasNextPage,
        isFetching: isLoadingProductList,
    } = useInfiniteQuery(
        productsInfiniteQueryOptions(
            search.department,
            search.category,
            search.appearance,
            search.size,
            search.design,
            search.collection,
            params.locale
        )
    )

    const products = useMemo(
        () => loadedProducts ?? initialProducts,
        [loadedProducts, initialProducts]
    )
    return {
        products,
        isLoading: isLoadingProductList,
        fetchNextPage,
        hasNextPage,
        search,
    }
}

type DesktopFilterProps = {
    onSelect: (filter: ProductFilters) => void
}

const DesktopFilter = ({onSelect}: DesktopFilterProps) => {
    const t = useTranslations('productList')
    const {filter, search} = useProductFilter()
    return (
        <Card className="py-4 md:sticky md:top-20 lg:top-[6.5rem] max-h-[calc(100svh_-_10rem)] overflow-y-auto">
            <h2 className="sr-only">{t('filter.title')}</h2>
            <Filter
                filter={filter}
                activeFilter={{
                    departmentId: search.department ?? undefined,
                    categoryId: search.category ?? undefined,
                    appearanceId: search.appearance ?? undefined,
                    sizeId: search.size ?? undefined,
                }}
                onSelect={onSelect}
            />
        </Card>
    )
}

const DesktopFilterSkeletonLoader = () => {
    const t = useTranslations('productList')
    return (
        <Card className="py-4 md:sticky md:top-20 lg:top-[6.5rem] max-h-[calc(100svh_-_10rem)] overflow-y-auto min-h-[50svh] animate-pulse">
            <span className="sr-only">{t('loading')}</span>
        </Card>
    )
}

type FilterBadgeListProps = {
    onSelect: (filter: ProductFilters) => void
}

const FilterBadgeList = ({onSelect}: FilterBadgeListProps) => {
    const {filter, search} = useProductFilter()
    return (
        <BaseFilterBadgeList
            filter={filter}
            activeFilter={{
                departmentId: search.department ?? undefined,
                categoryId: search.category ?? undefined,
                appearanceId: search.appearance ?? undefined,
                sizeId: search.size ?? undefined,
            }}
            onChange={onSelect}
        />
    )
}

const ProductList = () => {
    const t = useTranslations('productList')
    const params = useLocaleParams()
    const {isLoading, products, hasNextPage, fetchNextPage} = useProducts()

    const shopConfig = useShopConfig()
    const {data: defaultImages} = useSuspenseQuery(defaultImagesQueryOptions(params.locale))

    const productsWithImages = useMemo(() => {
        return (
            products?.map((product) => {
                const productTypeImage = defaultImages.find(
                    (image) => image.productTypeId === product.type?.id
                )?.images

                const baseProductImageSrc = `${shopConfig.imageServerEndpoint}/products/${product.vpId}`
                const viewId = getProductDefaultViewId(product.image?.src)!
                const appearanceId = getProductDefaultAppearanceId(product.image?.src)!

                return {
                    ...product,
                    image: {
                        ...product?.image,
                        src: getProductModelImageSrc(
                            baseProductImageSrc,
                            viewId,
                            appearanceId,
                            productTypeImage?.modelId,
                            productTypeImage?.crop
                        ),
                        srcset: getProductModelImageSrcSet(
                            baseProductImageSrc,
                            viewId,
                            appearanceId,
                            productTypeImage?.modelId,
                            productTypeImage?.crop
                        ),
                    },
                }
            }) ?? []
        )
    }, [defaultImages, shopConfig.imageServerEndpoint, products])

    const {ref: intersectionRef} = useIntersectionObserver({
        initialIsIntersecting: false,
        onChange: (isIntersecting) => {
            if (isIntersecting) {
                void fetchNextPage()
            }
        },
    })

    if (!isLoading && productsWithImages.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-lg text-stardust/70 mb-4">{t('emptyState')}</p>
            </div>
        )
    }

    return (
        <>
            <ul
                className={clsx(
                    'grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
                    isLoading && 'opacity-50 transition-opacity delay-500'
                )}
            >
                {productsWithImages?.map((product, index) => (
                    <li key={index}>
                        <Link
                            title={[product?.name, product?.type?.name].join(' | ')}
                            className="block h-full group outline-none"
                            to="/$lang/products/$productId"
                            params={{...params, productId: product?.id!}}
                            search={{
                                appearanceId: product?.defaultAppearanceId ?? undefined,
                            }}
                            preload={false}
                        >
                            <Card className="relative h-full flex flex-col group-hover:bg-gray-700/60 group-hover:border-nova/30 group-focus-visible:bg-gray-700/60 group-focus-visible:ring-2 ring-nova transition duration-200 ease-in-out transform-gpu">
                                <ImageComposition
                                    className="overflow-hidden"
                                    imageClassName="p-4 rounded scale-100 opacity-100 group-hover:scale-150 group-hover:pointer-events-none group-hover:opacity-40 group-focus-visible:scale-150 group-focus-visible:opacity-40 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out"
                                    image={product?.image!}
                                    lazy={index > 3}
                                    sizes="500px"
                                />
                                <div className="absolute opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 inset-0 p-4 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out">
                                    <ImageComposition
                                        className="rounded-md overflow-hidden bg-white"
                                        image={product?.designImage!}
                                        lazy={true}
                                        sizes="500px"
                                    />
                                </div>
                                <div className="flex gap-4 flex-row justify-between mx-4 mt-4 mb-2 text-sm flex-shrink-0">
                                    <div className="truncate text-gray-300">{product?.name}</div>
                                    <Price className="text-nowrap font-medium">
                                        {product?.price?.vatIncluded}
                                    </Price>
                                </div>
                                <div className="mx-4  mb-4 line-clamp-2 text-sm flex-grow">
                                    {product?.type?.name}
                                </div>
                            </Card>
                        </Link>
                    </li>
                ))}
            </ul>
            {hasNextPage && <div ref={intersectionRef} aria-hidden={true} className="opacity-0" />}
        </>
    )
}

const ProductListSkeletonLoader = () => {
    const t = useTranslations('productList')
    return (
        <>
            <div className="sr-only">{t('loading')}</div>
            <div
                aria-hidden={true}
                className="size-full grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            >
                <Card className="size-full aspect-square animate-pulse" />
                <Card className="size-full aspect-square animate-pulse" />
                <Card className="size-full aspect-square animate-pulse hidden sm:block" />
                <Card className="size-full aspect-square animate-pulse hidden sm:block" />
                <Card className="size-full aspect-square animate-pulse hidden lg:block" />
                <Card className="size-full aspect-square animate-pulse hidden lg:block" />
                <Card className="size-full aspect-square animate-pulse hidden lg:block" />
                <Card className="size-full aspect-square animate-pulse hidden lg:block" />
            </div>
        </>
    )
}
